"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { X, Share, Plus } from "lucide-react";
import { brandInitialLetter, shortAppName } from "@/lib/branding";
import { useStoreBranding } from "@/lib/supabase/siteSettings";
import { isPlatformHost } from "@/lib/platformHost";

// Banner ajakan install PWA (Add to Home Screen). Sadar-konteks: storefront →
// nama toko, /admin → "<Nama Toko> Admin" (akun cookie/sesi terpisah → dua app
// berbeda). Nama & logo mengikuti branding di Pengaturan Website (mig 196).
// - Android/Chromium: tangkap `beforeinstallprompt`, tampilkan tombol Pasang.
// - iOS/Safari: tak ada event → tampilkan instruksi Bagikan → Tambah ke Layar Utama.
// Disembunyikan bila sudah berjalan standalone atau baru saja ditutup pengguna.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "galea-install-dismissed";
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000; // jangan menggangu lagi selama 14 hari

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function dismissedRecently() {
  try {
    const t = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(t) && t > 0 && Date.now() - t < DISMISS_MS;
  } catch {
    return false;
  }
}

export function InstallPrompt() {
  const pathname = usePathname();
  const { storeName, logoUrl } = useStoreBranding();
  const isAdmin = pathname?.startsWith("/admin") ?? false;
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosHint, setIosHint] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Host platform punya popup pasang sendiri (PlatformInstallPrompt, khusus
    // panel). Tanpa ini iPhone melihat "Pasang Kelolify" bermerek toko di situs
    // jualan dan panel platform.
    if (isPlatformHost(window.location.hostname)) return;
    if (isStandalone() || dismissedRecently()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault(); // cegah mini-infobar bawaan; kita pakai banner sendiri
      setDeferred(e as BeforeInstallPromptEvent);
      setOpen(true);
    };
    const onInstalled = () => {
      setOpen(false);
      setDeferred(null);
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {
        /* abaikan */
      }
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari (bukan Chrome/Firefox/Edge iOS) → instruksi manual A2HS.
    const ua = window.navigator.userAgent;
    if (/iphone|ipad|ipod/i.test(ua) && /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua)) {
      setIosHint(true);
      setOpen(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!open) return null;

  const shortName = shortAppName(storeName);
  const appName = isAdmin ? `${shortName} Admin` : shortName;
  // Aksen banner ikut WARNA TEMA toko; admin memakai turunan gelap agar
  // pembedaan Admin vs Pelanggan yang sudah ada tetap terjaga lintas tema.
  const accent = isAdmin ? "bg-shopeeDark" : "bg-shopee";

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* abaikan */
    }
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* abaikan */
    }
    setDeferred(null);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-label={`Pasang ${appName}`}
      className="fixed inset-x-0 bottom-20 z-50 mx-auto w-[calc(100%-1.5rem)] max-w-sm rounded-2xl border border-neutral-100 bg-white p-3 shadow-soft"
    >
      <div className="flex items-start gap-3">
        {logoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={logoUrl}
            alt=""
            aria-hidden="true"
            className="h-10 w-10 shrink-0 rounded-xl object-contain"
          />
        ) : (
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent} text-base font-extrabold text-white`}
            aria-hidden="true"
          >
            {brandInitialLetter(storeName)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">Pasang {appName}</p>
          {iosHint ? (
            <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs leading-relaxed text-muted">
              Ketuk <Share className="inline h-3.5 w-3.5" aria-label="Bagikan" /> lalu
              <span className="inline-flex items-center gap-0.5 font-medium text-ink">
                <Plus className="h-3.5 w-3.5" /> Tambah ke Layar Utama
              </span>
            </p>
          ) : (
            <p className="mt-0.5 text-xs leading-relaxed text-muted">
              Akses lebih cepat dari layar utama, tanpa buka browser.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Tutup"
          className="shrink-0 rounded-full p-1 text-muted hover:bg-neutral-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {!iosHint && (
        <button
          type="button"
          onClick={install}
          className={`mt-3 w-full rounded-full ${accent} py-2 text-sm font-semibold text-white`}
        >
          Pasang sekarang
        </button>
      )}
    </div>
  );
}
